/**
 * AI Agents Module
 *
 * Central export for all AI agents in the Marta system.
 *
 * Available agents:
 * - M.E.A. (Marta Email Assistant) - Email classification
 * - M.P.M. (Marta Projects Manager) - Project intelligence
 * - M.C.A. (Marta Calendar Assistant) - Calendar intelligence
 * - M.H.A. (Marta Hub Assistant) - Communication intelligence
 * - M.F.A. (Marta Finance Assistant) - Financial intelligence
 * - M.S.A. (Marta Support Assistant) - Support triage
 */

// Base types for all agents
export * from './types.js';

// Agent registry
export {
  registerAgent,
  getAgent,
  getAllAgents,
  getEnabledAgents,
  setAgentEnabled,
  getAgentMetrics,
  updateAgentMetrics,
  recordCorrection,
  computeMetrics,
  findAgentsByTrigger,
  findAgentsByCapability,
} from './registry.js';

// M.E.A. (Marta Email Assistant)
export * as mea from './marta/index.js';
export { MEA_AGENT, MEA_AGENT_ID } from './marta/config.js';

// M.P.M. (Marta Projects Manager)
export * as mpm from './mpm/index.js';
export { MPM_AGENT, MPM_AGENT_ID } from './mpm/config.js';

// M.C.A. (Marta Calendar Assistant)
export * as mca from './mca/index.js';
export { MCA_AGENT, MCA_AGENT_ID } from './mca/config.js';

// M.H.A. (Marta Hub Assistant)
export * as mha from './mha/index.js';
export { MHA_AGENT, MHA_AGENT_ID } from './mha/config.js';

// M.F.A. (Marta Finance Assistant)
export * as mfa from './mfa/index.js';
export { MFA_AGENT, MFA_AGENT_ID } from './mfa/config.js';

// M.S.A. (Marta Support Assistant)
export * as msa from './msa/index.js';
export { MSA_AGENT, MSA_AGENT_ID } from './msa/config.js';

// =============================================================================
// INITIALIZATION
// =============================================================================

import { registerAgent } from './registry.js';
import { MEA_AGENT } from './marta/config.js';
import { MPM_AGENT } from './mpm/config.js';
import { MCA_AGENT } from './mca/config.js';
import { MHA_AGENT } from './mha/config.js';
import { MFA_AGENT } from './mfa/config.js';
import { MSA_AGENT } from './msa/config.js';

/**
 * Initialize all agents in the registry.
 * Call this once at application startup.
 */
export function initializeAgents(): void {
  registerAgent(MEA_AGENT);
  registerAgent(MPM_AGENT);
  registerAgent(MCA_AGENT);
  registerAgent(MHA_AGENT);
  registerAgent(MFA_AGENT);
  registerAgent(MSA_AGENT);
}

/**
 * Get all agent definitions
 */
export function getAllAgentDefinitions() {
  return [MEA_AGENT, MPM_AGENT, MCA_AGENT, MHA_AGENT, MFA_AGENT, MSA_AGENT];
}

/**
 * Get count of available agents
 */
export function getAgentCount(): number {
  return getAllAgentDefinitions().length;
}
